import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Bug, X } from 'lucide-react';
import { db, getPendingWrites } from '../../lib/db';
import { triggerSync } from '../../services/networkMonitor';

/**
 * Developer panel for inspecting the offline queue in a real browser.
 *
 * Hidden unless explicitly enabled, because it exposes raw payloads and a destructive
 * clear button — neither belongs on a device in a hotel corridor. Enable with
 * VITE_SHOW_OUTBOX_INSPECTOR=true, or by setting `nbh.debug` in localStorage so a
 * production build can still be inspected on the device where a bug is happening.
 */
export function isInspectorEnabled(): boolean {
  if (import.meta.env.VITE_SHOW_OUTBOX_INSPECTOR === 'true') return true;
  try {
    return localStorage.getItem('nbh.debug') === 'true';
  } catch {
    return false;
  }
}

export function OutboxInspector() {
  const [open, setOpen] = useState(false);
  const pending = useLiveQuery(getPendingWrites, [], []);

  if (!isInspectorEnabled()) return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open outbox inspector"
        data-testid="outbox-inspector-open"
        className="fixed bottom-28 right-3 z-50 flex size-12 min-h-0 items-center justify-center rounded-full bg-fuchsia-700 p-0 text-white shadow-lg"
      >
        <Bug className="size-5" aria-hidden="true" />
        {pending.length > 0 && (
          <span className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold">
            {pending.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <aside
      data-testid="outbox-inspector"
      className="fixed inset-x-3 bottom-28 z-50 max-h-[60vh] overflow-auto rounded-xl border border-fuchsia-700 bg-slate-950/98 p-3 text-xs shadow-2xl"
    >
      <header className="mb-2 flex items-center justify-between">
        <span className="font-semibold text-fuchsia-300">Outbox inspector</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close outbox inspector"
          className="flex size-8 min-h-0 items-center justify-center bg-transparent p-0 text-slate-400"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </header>

      <p className="mb-2 flex items-center gap-2">
        <span
          data-testid="inspector-online"
          data-online={navigator.onLine}
          className={`size-2.5 rounded-full ${navigator.onLine ? 'bg-emerald-400' : 'bg-red-500'}`}
        />
        <span className="text-slate-300">
          {navigator.onLine ? 'Online' : 'Offline'} · {pending.length} queued
        </span>
      </p>

      <div className="mb-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void triggerSync()}
          data-testid="inspector-force-sync"
          className="min-h-[36px] rounded bg-sky-700 px-2 text-xs text-white"
        >
          Force sync now
        </button>
        <button
          type="button"
          onClick={() => void db.outbox.clear()}
          data-testid="inspector-clear"
          className="min-h-[36px] rounded bg-red-800 px-2 text-xs text-white"
        >
          Clear outbox
        </button>
      </div>

      {pending.length === 0 ? (
        <p className="text-slate-500">Queue is empty.</p>
      ) : (
        <ul className="space-y-2" data-testid="inspector-items">
          {pending.map((entry) => (
            <li key={entry.id} className="rounded border border-slate-800 p-2">
              <p className="text-slate-300">
                {entry.method} {entry.path} · {entry.status}
                {entry.attempts > 0 && ` · ${entry.attempts} attempt(s)`}
              </p>
              {/* reportedAt is the whole point of the queue, so it is shown first. */}
              <p className="text-amber-300">
                reportedAt: {String((entry.body as { reportedAt?: unknown })?.reportedAt ?? '—')}
              </p>
              <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all text-[10px] text-slate-500">
                {JSON.stringify(entry.body)}
              </pre>
              {entry.lastError && <p className="text-red-400">{entry.lastError}</p>}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
